/**
 * F17.2.11 — Scheda tecnica formale Controsoffitto.
 *
 * Mirror di `TechnicalSheet.tsx` (wall) ma con:
 *  - Composizione struttura ceiling-specific (drop, hangerSpacing primario/perpendicolare,
 *    primarySpacing, secondarySpacing, perimeter, pendini/m²)
 *  - Voce di capitolato per controsoffitto a secco
 *  - Spessore = altezza pacchetto sospeso (model.totalDepthMm)
 *
 * Sub-componenti `PerformanceCell` / `SpecRow` / `CategoryBadge` sono duplicati
 * per non toccare il wall (refactor di estrazione → eventuale F19 di tidy-up).
 *
 * Export PDF: rimandato a F17.2.16 (servirà jsPDF template ceiling-specific).
 * Per ora bottone disabilitato con tooltip.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Flame, Volume2, Thermometer, Weight, Ruler, Wrench, BookOpen, Download, Anchor } from 'lucide-react';
import { toast } from 'sonner';
import type { LayerV2 } from '../../types';
import type { CeilingSectionModel } from '../section-view/ceilingModel';
import type { CeilingDimensionalState } from '../../hooks/useCeilingConfiguratorState';
import { exportCeilingTechnicalSheetPDF } from './exportCeilingTechnicalSheetPDF';
import { formatNumber } from '@/utils/format';

interface CeilingTechnicalSheetProps {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  model: CeilingSectionModel;
  /** Codice / titolo controsoffitto (es. "Controsoffitto pendinato orditura doppia"). */
  ceilingTitle?: string | null;
  /** Nome stratigrafia per export PDF. */
  stratigraphyName?: string;
  /** Descrizione stratigrafia per export PDF. */
  stratigraphyDescription?: string;
  /** Costo €/m² totale (esclude perimetro). */
  totalCostPerSqm?: number;
}

const CeilingTechnicalSheet: React.FC<CeilingTechnicalSheetProps> = ({
  layers,
  ceiling,
  model,
  ceilingTitle,
  stratigraphyName,
  stratigraphyDescription,
  totalCostPerSqm = 0,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const sectionSvg = document.querySelector<SVGSVGElement>(
        '[data-radix-tabs-content][data-state="active"] svg.technical-ceiling-svg, .technical-ceiling-svg, [data-ceiling-section-view] svg',
      );
      await exportCeilingTechnicalSheetPDF({
        layers,
        ceiling,
        model,
        ceilingTitle: ceilingTitle ?? undefined,
        stratigraphyName: stratigraphyName ?? 'Controsoffitto',
        stratigraphyDescription,
        totalCostPerSqm,
        sectionSvgElement: sectionSvg,
      });
      toast.success('PDF scaricato.');
    } catch (err) {
      console.error('[CeilingTechnicalSheet] export PDF error:', err);
      toast.error('Errore durante la generazione del PDF.');
    } finally {
      setIsExporting(false);
    }
  };

  // F17.2.17: TUTTI gli hook useMemo DEVONO essere chiamati PRIMA di qualsiasi
  // early return per rispettare le React Hooks rules (ordine costante).
  // L'early return per layers vuoti sta sotto.

  // ===== Aggregati prestazioni =====
  const totalWeight = useMemo(() => layers.reduce((acc, l) => {
    const w = Number(l.material?.weight_per_sqm ?? 0);
    return acc + (Number.isFinite(w) ? w : 0);
  }, 0), [layers]);

  // ===== Conteggi categorie =====
  // Distinzione primary/secondary frame: primo frame visto = primario, successivi = secondari
  // (stessa logica di buildCeilingSectionModel).
  const frameLayersWithLevel = useMemo(() => {
    let frameCount = 0;
    return layers.map((l) => {
      if (l.material?.category === 'structure_frame') {
        frameCount += 1;
        return { layer: l, level: frameCount === 1 ? 'primary' as const : 'secondary' as const };
      }
      return { layer: l, level: null };
    });
  }, [layers]);

  // Quick-lookup id→level per il render della tabella stratigrafia
  const levelByLayerId = useMemo(() => {
    const m = new Map<string, 'primary' | 'secondary'>();
    frameLayersWithLevel.forEach(({ layer, level }) => {
      if (level) m.set(layer.id, level);
    });
    return m;
  }, [frameLayersWithLevel]);

  if (layers.length === 0) {
    return (
      <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
        <div>
          <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
          <p>Aggiungi materiali per generare la scheda tecnica</p>
        </div>
      </Card>
    );
  }

  // ===== Aggregati prestazioni (no-hook, recalcolati ad ogni render) =====
  const lambdaLayers = layers.filter(l => Number(l.material?.thermal_conductivity ?? 0) > 0);
  const avgLambda = lambdaLayers.length > 0
    ? lambdaLayers.reduce((acc, l) => {
        const lam = Number(l.material?.thermal_conductivity ?? 0);
        const t = Number(l.thickness ?? l.material?.thickness ?? 0);
        return acc + lam * t;
      }, 0) / lambdaLayers.reduce((acc, l) => acc + Number(l.thickness ?? l.material?.thickness ?? 0), 0)
    : null;

  const bestRw = layers.reduce((acc, l) => {
    const r = Number(l.material?.acoustic_performance ?? 0);
    return r > acc ? r : acc;
  }, 0);

  const fireClasses = Array.from(new Set(
    layers
      .map(l => l.material?.fire_class)
      .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
  ));

  const boards = layers.filter(l => l.material?.category === 'board' || l.material?.category === 'ceiling_tile');
  const primaryFrames = frameLayersWithLevel.filter(x => x.level === 'primary');
  const secondaryFrames = frameLayersWithLevel.filter(x => x.level === 'secondary');
  const insulations = layers.filter(l => l.material?.category === 'insulation');
  const screwsLayers = layers.filter(l => l.screwMaterial && (l.screwQuantity ?? 0) > 0);

  const isSuspended = ceiling.attachmentType === 'suspended';
  const totalThicknessMm = model.totalDepthMm;

  // ===== Voce di capitolato =====
  const capitolato = (() => {
    const parts: string[] = [];
    parts.push('Realizzazione di controsoffitto a secco costituito da:');
    if (isSuspended) {
      parts.push(
        ` - struttura sospesa al solaio mediante pendini regolabili a passo ${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm}mm,` +
        ` con camera tecnica di altezza ${ceiling.ceilingDropMm}mm;`
      );
    } else {
      parts.push(' - struttura fissata direttamente al solaio (sistema aderente);');
    }
    if (primaryFrames.length > 0) {
      const pf = primaryFrames[0].layer.material;
      parts.push(
        ` - profili portanti primari a C in acciaio zincato larghezza ${pf?.width ?? '—'}mm` +
        (pf?.sheet_thickness ? ` (lamiera ${pf.sheet_thickness}mm)` : '') +
        `, posati ad interasse ${ceiling.primarySpacingMm}mm;`
      );
    }
    if (secondaryFrames.length > 0) {
      const sf = secondaryFrames[0].layer.material;
      const sp = ceiling.secondarySpacingMm ?? 500;
      parts.push(
        ` - profili portanti secondari a C larghezza ${sf?.width ?? '—'}mm` +
        (sf?.sheet_thickness ? ` (lamiera ${sf.sheet_thickness}mm)` : '') +
        `, orditi perpendicolarmente ai primari ad interasse ${sp}mm;`
      );
    }
    if (insulations.length > 0) {
      const ins = insulations[0].material;
      const t = insulations[0].thickness ?? ins?.thickness ?? 0;
      parts.push(
        ` - materassino isolante in ${ins?.material_type ?? 'fibra minerale'}` +
        ` spessore ${t}mm` +
        (ins?.thermal_conductivity ? ` (λ=${ins.thermal_conductivity} W/mK)` : '') +
        `, posato sull'orditura;`
      );
    }
    if (boards.length > 0) {
      const boardCount = boards.length;
      const firstBoard = boards[0].material;
      parts.push(
        ` - rivestimento a vista in n.${boardCount} lastre di ${firstBoard?.material_type ?? 'cartongesso'}` +
        ` spessore ${firstBoard?.thickness ?? '12,5'}mm` +
        (firstBoard?.en_520_type ? ` tipo ${firstBoard.en_520_type} (UNI EN 520)` : '') +
        `;`
      );
    }
    if (screwsLayers.length > 0) {
      parts.push(
        ` - fissaggio delle lastre con viti autofilettanti per cartongesso, interasse conforme a UNI 11424:2015.`
      );
    }
    if (ceiling.perimeterProfileMlPerSqm && ceiling.perimeterProfileMlPerSqm > 0) {
      parts.push(
        ` Profilo perimetrale a U installato lungo tutto il perimetro della stanza.`
      );
    }
    parts.push(
      ` Lo spessore complessivo del pacchetto sospeso è di ${totalThicknessMm}mm.`
    );
    return parts.join('').trim();
  })();

  return (
    <Card className="p-0 overflow-hidden bg-white">
      {/* === HEADER === */}
      <div className="px-6 py-5 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
              Scheda tecnica · DryConfig · Controsoffitto
            </div>
            <h2 className="text-2xl font-bold tracking-tight">
              {stratigraphyName ?? 'CS-CUSTOM'}
            </h2>
            <p className="text-sm text-zinc-300 mt-1">
              {ceilingTitle ?? 'Controsoffitto a secco'}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportPdf}
              disabled={isExporting || layers.length === 0}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {isExporting ? 'Esporto…' : 'Esporta PDF'}
            </Button>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Pacchetto</div>
              <div className="text-3xl font-bold font-mono">{totalThicknessMm}</div>
              <div className="text-[10px] text-zinc-400">mm</div>
            </div>
          </div>
        </div>
      </div>

      {/* === PRESTAZIONI === */}
      <div className="grid grid-cols-4 divide-x border-b">
        <PerformanceCell
          icon={<Flame className="h-4 w-4 text-orange-600" />}
          label="Resistenza al fuoco"
          value={fireClasses.length > 0 ? fireClasses.join(' / ') : '—'}
          unit="EI / classe"
        />
        <PerformanceCell
          icon={<Volume2 className="h-4 w-4 text-blue-600" />}
          label="Isolamento acustico"
          value={bestRw > 0 ? formatNumber(bestRw, 0) : '—'}
          unit="Rw dB"
        />
        <PerformanceCell
          icon={<Thermometer className="h-4 w-4 text-emerald-600" />}
          label="Cond. termica media"
          value={avgLambda !== null ? formatNumber(avgLambda, 3) : '—'}
          unit="λ W/mK"
        />
        <PerformanceCell
          icon={<Weight className="h-4 w-4 text-zinc-700" />}
          label="Peso specifico"
          value={totalWeight > 0 ? formatNumber(totalWeight, 1) : '—'}
          unit="kg/m²"
        />
      </div>

      {/* === STRATIGRAFIA === */}
      <div className="px-6 py-5 border-b">
        <div className="flex items-center gap-2 mb-3">
          <Ruler className="h-4 w-4 text-zinc-700" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-900">
            Stratigrafia (solaio → vista)
          </h3>
        </div>
        <div className="overflow-hidden border border-zinc-200 rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left w-10">N°</th>
                <th className="px-3 py-2 text-left">Materiale</th>
                <th className="px-3 py-2 text-left">Categoria</th>
                <th className="px-3 py-2 text-right">Spessore</th>
                <th className="px-3 py-2 text-right">Peso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {layers.map((l, idx) => {
                const m = l.material;
                if (!m) {
                  return (
                    <tr key={l.id} className="text-zinc-400 italic">
                      <td className="px-3 py-2 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2" colSpan={4}>(layer vuoto)</td>
                    </tr>
                  );
                }
                const t = l.thickness ?? m.thickness ?? 0;
                const w = m.weight_per_sqm;
                return (
                  <tr key={l.id} className="hover:bg-zinc-50/50">
                    <td className="px-3 py-2 font-mono text-zinc-500">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-zinc-900">{m.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {m.supplier ?? '—'}{m.code ? ` · ${m.code}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <CategoryBadge category={m.category} level={levelByLayerId.get(l.id) ?? null} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-700">
                      {t > 0 ? `${formatNumber(Number(t), 1)} mm` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-700">
                      {w ? `${formatNumber(Number(w), 2)} kg/m²` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-zinc-50 font-semibold">
              <tr>
                <td className="px-3 py-2" colSpan={3}>Totale pacchetto sospeso</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-900">
                  {totalThicknessMm} mm
                </td>
                <td className="px-3 py-2 text-right font-mono text-zinc-900">
                  {formatNumber(totalWeight, 2)} kg/m²
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* === COMPOSIZIONE STRUTTURA CEILING-SPECIFIC === */}
      <div className="px-6 py-5 border-b">
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="h-4 w-4 text-zinc-700" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-900">
            Composizione struttura
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <SpecRow label="Tipologia" value="Controsoffitto a secco" />
          <SpecRow label="Sospensione" value={isSuspended ? 'Pendinato' : 'Aderente al solaio'} />
          {isSuspended && (
            <>
              <SpecRow label="Camera tecnica (drop)" value={`${ceiling.ceilingDropMm} mm`} />
              <SpecRow
                label="Passo pendini"
                value={`${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm} mm`}
              />
              {model.hangerCountPerSqm !== null && (
                <SpecRow
                  label="Densità pendini"
                  value={`${formatNumber(model.hangerCountPerSqm, 2)} pz/m²`}
                />
              )}
            </>
          )}
          <SpecRow label="Orditura" value={model.hasDoubleStructure ? 'Doppia (primaria + secondaria)' : 'Singola'} />
          <SpecRow label="Passo primaria" value={`${ceiling.primarySpacingMm} mm`} />
          {model.hasDoubleStructure && (
            <SpecRow label="Passo secondaria" value={`${ceiling.secondarySpacingMm ?? 500} mm`} />
          )}
          <SpecRow label="N. lastre" value={`${boards.length}`} />
          <SpecRow label="N. orditura primaria" value={`${primaryFrames.length}`} />
          <SpecRow label="N. orditura secondaria" value={`${secondaryFrames.length}`} />
          <SpecRow label="N. isolanti" value={`${insulations.length}`} />
          {ceiling.perimeterProfileMlPerSqm !== null && ceiling.perimeterProfileMlPerSqm > 0 && (
            <SpecRow
              label="Perimetro"
              value={`${formatNumber(ceiling.perimeterProfileMlPerSqm, 2)} ml/m²`}
            />
          )}
          {screwsLayers.length > 0 && (
            <SpecRow
              label="Viti per fissaggio"
              value={`${screwsLayers.length} layer · ${formatNumber(screwsLayers.reduce((acc, l) => acc + (l.screwQuantity ?? 0), 0), 0)} pz/m²`}
            />
          )}
        </div>
      </div>

      {/* === VOCE DI CAPITOLATO === */}
      <div className="px-6 py-5 border-b bg-zinc-50/50">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="h-4 w-4 text-zinc-700" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-900">
            Voce di capitolato
          </h3>
        </div>
        <div className="text-xs leading-relaxed text-zinc-700 bg-white border border-zinc-200 rounded-md p-4 font-serif">
          {capitolato}
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 italic">
          Testo generato automaticamente · da rivedere e adattare alle specifiche del cantiere prima dell'inserimento in capitolato.
        </p>
      </div>

      {/* === COSTO === */}
      {totalCostPerSqm > 0 && (
        <div className="px-6 py-4 border-b bg-emerald-50/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Anchor className="h-4 w-4 text-emerald-700" />
              <span className="text-xs uppercase tracking-wider text-zinc-600 font-semibold">
                Costo stimato pacchetto
              </span>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold font-mono text-emerald-700">
                {formatNumber(totalCostPerSqm, 2)}
              </span>
              <span className="text-xs text-zinc-600 ml-1">€/m²</span>
            </div>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1 italic">
            Esclude profilo perimetrale (calcolato al momento dell'aggiunta al preventivo, in base al perimetro stanza).
          </p>
        </div>
      )}

      {/* === NORME === */}
      <div className="px-6 py-4">
        <h3 className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">
          Norme di riferimento
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-zinc-600">
          <div>· UNI 11424:2015 — Sistemi a secco con lastre di gesso rivestito</div>
          <div>· UNI EN 520 — Lastre di gesso rivestito (tipi e proprietà)</div>
          <div>· UNI EN 13964 — Controsoffitti: requisiti e metodi di prova</div>
          <div>· UNI EN 13501-1 — Classificazione di reazione al fuoco</div>
          <div>· UNI EN ISO 717-1 — Valutazione isolamento acustico</div>
          <div>· UNI EN 14195 — Profili metallici per sistemi a secco</div>
        </div>
      </div>

      {/* === FOOTER === */}
      <div className="px-6 py-3 bg-zinc-50 border-t text-[10px] text-zinc-500 flex items-center justify-between">
        <span>Documento generato da DryConfig</span>
        <span className="font-mono">{new Date().toLocaleDateString('it-IT')}</span>
      </div>
    </Card>
  );
};

// ===== Sub-componenti =====

const PerformanceCell: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  unit: string;
}> = ({ icon, label, value, unit }) => (
  <div className="px-4 py-4 text-center">
    <div className="flex items-center justify-center gap-1 mb-1">
      {icon}
      <span className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</span>
    </div>
    <div className="text-2xl font-bold font-mono text-zinc-900">{value}</div>
    <div className="text-[10px] text-zinc-500">{unit}</div>
  </div>
);

const SpecRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="flex justify-between items-baseline border-b border-zinc-100 py-1">
    <span className="text-zinc-500">{label}</span>
    <span className="font-mono font-medium text-zinc-900">{value}</span>
  </div>
);

const CategoryBadge: React.FC<{ category: string; level?: 'primary' | 'secondary' | null }> = ({ category, level }) => {
  // Override etichetta per i ruoli ceiling-specific (calcolati via ordine, non via campo DB)
  if (level === 'primary') {
    return <Badge variant="outline" className="bg-zinc-100 text-zinc-700 border-zinc-300 text-[10px] font-medium px-1.5 py-0">Orditura primaria</Badge>;
  }
  if (level === 'secondary') {
    return <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] font-medium px-1.5 py-0">Orditura secondaria</Badge>;
  }
  const map: Record<string, { label: string; cls: string }> = {
    board:           { label: 'Lastra',     cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    ceiling_tile:    { label: 'Pannello',   cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    structure_frame: { label: 'Orditura',   cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
    structure_guide: { label: 'Guida',      cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
    insulation:      { label: 'Isolante',   cls: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
    screw:           { label: 'Vite',       cls: 'bg-blue-50 text-blue-800 border-blue-200' },
    finish:          { label: 'Finitura',   cls: 'bg-purple-50 text-purple-800 border-purple-200' },
  };
  const conf = map[category] ?? { label: category, cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' };
  return (
    <Badge variant="outline" className={`${conf.cls} text-[10px] font-medium px-1.5 py-0`}>
      {conf.label}
    </Badge>
  );
};

export default CeilingTechnicalSheet;
